import { useParams } from "react-router-dom";

import { useLanguage } from "../../../context/LanguageProvider";
import { ROUTES } from "../../../shared/constants/routes";
import StatusMessage from "../../../shared/components/elements/StatusMessage";
import PageTitle from "../../../shared/components/layout/PageTitle";
import SEO from "../../../shared/components/layout/SEO";
import AccountLayout from "../../account/components/AccountLayout";
import { ACCOUNT_MESSAGES } from "../../account/messages";

import CheatsheetRenderer from "../components/CheatsheetRenderer";
import { useCheatsheet } from "../hooks/useCheatsheet";
import { CHEATSHEET_MESSAGES } from "../messages";

const CheatsheetPage = () => {
    const { course_id, cheatsheet_id } = useParams<{ course_id: string; cheatsheet_id: string }>();
    const { lang, t } = useLanguage();
    const { data: cheatsheet, loading, error } = useCheatsheet(course_id, cheatsheet_id);

    const breadcrumb = [
        { title: ACCOUNT_MESSAGES.account, link: "#" },
        { title: ACCOUNT_MESSAGES.myCourses, link: ROUTES.myCourses(lang) },
        {
            title: CHEATSHEET_MESSAGES.cheatsheet,
            link: course_id ? ROUTES.myCourseCheatsheet(lang, course_id) : ""
        },
        { title: cheatsheet?.title || CHEATSHEET_MESSAGES.cheatsheet, link: "" }
    ];

    const renderContent = () => {
        if (loading) return <StatusMessage>{CHEATSHEET_MESSAGES.loading}</StatusMessage>;
        if (error) return <StatusMessage>{CHEATSHEET_MESSAGES.failed}</StatusMessage>;

        if (!cheatsheet) {
            return (
                <div className="bg-gray-100 rounded-xl p-8 text-center text-gray-500">
                    {CHEATSHEET_MESSAGES.notFound}
                </div>
            );
        }

        return (
            <>
                <PageTitle title={cheatsheet.title} />

                {cheatsheet.subtitle && (
                    <p className="mt-2 mb-8 text-gray-500">{cheatsheet.subtitle}</p>
                )}

                <div className="bg-white border border-gray-200 rounded-xl p-6 mt-6">
                    <CheatsheetRenderer content={cheatsheet.content} />
                </div>
            </>
        );
    };

    return (
        <>
            <SEO
                title={`${cheatsheet?.title ?? CHEATSHEET_MESSAGES.cheatsheet} | ${t.nav.brand}`}
                description={cheatsheet?.subtitle || CHEATSHEET_MESSAGES.seoDescription}
                path={`/account/course/${course_id}/cheatsheet/${cheatsheet_id}`}
                noIndex
            />

            <AccountLayout breadcrumb={breadcrumb}>{renderContent()}</AccountLayout>
        </>
    );
};

export default CheatsheetPage;